import React, { useState, useEffect } from 'react';
import { auth } from '../firebase/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar
} from '@mui/material';
import { 
  Add, 
  Delete, 
  Edit
} from '@mui/icons-material';

interface Member {
  name: string;
  createdAt: Date;
  isActive: boolean;
  icon?: string;
}

interface MembersData {
  [key: string]: Member | null;
}

interface ScoreService {
  getMembers: () => Promise<MembersData>;
  addMember: (name: string, memberId?: string | null) => Promise<string | null>;
  removeMember: (memberId: string) => Promise<boolean>;
  updateMemberIcon: (memberId: string, icon: string) => Promise<boolean>;
  updateMemberName: (memberId: string, name: string) => Promise<boolean>;
  getScore: (memberId: string | null) => Promise<number>;
  saveScore: (score: number, memberId: string | null) => Promise<number>;
}

interface MembersProps {
  scoreService: ScoreService;
  onMembersUpdate: () => void;
}

// Available emoji icons for members - kid-friendly collection
const availableEmojis = {
  // Faces & People
  '😊': { emoji: '😊', name: 'Happy' },
  '😎': { emoji: '😎', name: 'Cool' },
  '🤗': { emoji: '🤗', name: 'Hug' },
  '😜': { emoji: '😜', name: 'Silly' },
  '🥳': { emoji: '🥳', name: 'Party' },
  '👶': { emoji: '👶', name: 'Baby' },
  
  // Animals
  '🐕': { emoji: '🐕', name: 'Dog' },
  '🐱': { emoji: '🐱', name: 'Cat' },
  '🐰': { emoji: '🐰', name: 'Bunny' },
  '🐻': { emoji: '🐻', name: 'Bear' },
  '🐸': { emoji: '🐸', name: 'Frog' },
  '🐧': { emoji: '🐧', name: 'Penguin' },
  '🦄': { emoji: '🦄', name: 'Unicorn' },
  '🐼': { emoji: '🐼', name: 'Panda' },
  '🐯': { emoji: '🐯', name: 'Tiger' },
  '🦁': { emoji: '🦁', name: 'Lion' },
  '🐒': { emoji: '🐒', name: 'Monkey' },
  '🐘': { emoji: '🐘', name: 'Elephant' },
  
  // Activities & Sports
  '⚽': { emoji: '⚽', name: 'Soccer' },
  '🏀': { emoji: '🏀', name: 'Basketball' },
  '🎮': { emoji: '🎮', name: 'Gaming' },
  '🎨': { emoji: '🎨', name: 'Art' },
  '🎵': { emoji: '🎵', name: 'Music' },
  '📚': { emoji: '📚', name: 'Reading' },
  '🏊': { emoji: '🏊', name: 'Swimming' },
  '🚴': { emoji: '🚴', name: 'Biking' },
  
  // Fun & Fantasy
  '🚀': { emoji: '🚀', name: 'Rocket' },
  '⭐': { emoji: '⭐', name: 'Star' },
  '🌈': { emoji: '🌈', name: 'Rainbow' },
  '🎪': { emoji: '🎪', name: 'Circus' },
  '🎠': { emoji: '🎠', name: 'Carousel' },
  '🎭': { emoji: '🎭', name: 'Theater' },
  '🎲': { emoji: '🎲', name: 'Dice' },
  
  // Food (kids love food!)
  '🍕': { emoji: '🍕', name: 'Pizza' },
  '🍦': { emoji: '🍦', name: 'Ice Cream' },
  '🍪': { emoji: '🍪', name: 'Cookie' },
  '🍰': { emoji: '🍰', name: 'Cake' },
  '🍎': { emoji: '🍎', name: 'Apple' },
  '🍌': { emoji: '🍌', name: 'Banana' },
  
  // Transportation & Adventure
  '🚗': { emoji: '🚗', name: 'Car' },
  '✈️': { emoji: '✈️', name: 'Airplane' },
  '🚂': { emoji: '🚂', name: 'Train' },
  '🛸': { emoji: '🛸', name: 'UFO' },
  '🏰': { emoji: '🏰', name: 'Castle' },
  
  // Nature & Flowers
  '🌸': { emoji: '🌸', name: 'Flower' },
  '🌻': { emoji: '🌻', name: 'Sunflower' },
  '🌺': { emoji: '🌺', name: 'Hibiscus' },
  '🍀': { emoji: '🍀', name: 'Clover' },
  
  // Love & Hearts
  '❤️': { emoji: '❤️', name: 'Heart' },
  '💖': { emoji: '💖', name: 'Pink Heart' },
  '💙': { emoji: '💙', name: 'Blue Heart' },
  '💚': { emoji: '💚', name: 'Green Heart' },
  
  // Magic & Wonder
  '✨': { emoji: '✨', name: 'Sparkles' },
  '🌟': { emoji: '🌟', name: 'Glowing Star' },
  '🔮': { emoji: '🔮', name: 'Crystal Ball' },
  '🎆': { emoji: '🎆', name: 'Fireworks' }
};

const Members: React.FC<MembersProps> = ({ 
  scoreService, 
  onMembersUpdate 
}) => {
  const [members, setMembers] = useState<MembersData>({});
  const [showAddMember, setShowAddMember] = useState<boolean>(false);
  const [newMemberName, setNewMemberName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [editMemberOpen, setEditMemberOpen] = useState<boolean>(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [currentScore, setCurrentScore] = useState<number>(0);
  const [currentScoreInput, setCurrentScoreInput] = useState<string>('0');
  const [editMemberName, setEditMemberName] = useState<string>('');
  const [selectedIcon, setSelectedIcon] = useState<string>('😊');

  useEffect(() => {
    loadMembers();
  }, []);

  // Reload members when authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('DEBUG: User signed in, reloading members...');
        loadMembers();
      }
    });

    return () => unsubscribe();
  }, []);

  const loadMembers = async () => {
    try {
      console.log('DEBUG: MemberManagement loading members...');
      const membersData = await scoreService.getMembers();
      console.log('DEBUG: MemberManagement received members:', membersData);
      setMembers(membersData);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  const handleAddMember = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newMemberName.trim()) return;

    setLoading(true);
    try {
      const newMemberId = await scoreService.addMember(newMemberName.trim());
      if (newMemberId) {
        await loadMembers();
        setNewMemberName('');
        setShowAddMember(false);
        if (onMembersUpdate) {
          onMembersUpdate();
        }
      }
    } catch (error) {
      console.error('Error adding member:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (Object.keys(members).length <= 1) {
      alert('Cannot remove the last member.');
      return;
    }

    if (window.confirm('Are you sure you want to remove this member? This will delete all their scores.')) {
      setLoading(true);
      try {
        const success = await scoreService.removeMember(memberId);
        if (success) {
          await loadMembers();
          if (onMembersUpdate) {
            onMembersUpdate();
          }
        }
      } catch (error) {
        console.error('Error removing member:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditMember = async (memberId: string) => {
    setSelectedMemberId(memberId);
    setLoading(true);
    try {
      const score = await scoreService.getScore(memberId);
      const memberName = members[memberId]?.name || '';
      const memberIcon = members[memberId]?.icon || '😊';
      setCurrentScore(score);
      setCurrentScoreInput(score.toString());
      setEditMemberName(memberName);
      setSelectedIcon(memberIcon);
      setEditMemberOpen(true);
    } catch (error) {
      console.error('Error loading member data:', error);
      setCurrentScore(0);
      setCurrentScoreInput('0');
      setEditMemberName(members[memberId]?.name || '');
      setSelectedIcon(members[memberId]?.icon || '😊');
      setEditMemberOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleIconSelection = (emoji: string) => {
    setSelectedIcon(emoji);
  };

  const handleSaveChanges = async () => {
    if (!selectedMemberId) return;
    
    setLoading(true);
    try {
      // Update name if it changed and is valid
      if (editMemberName.trim() && editMemberName.trim() !== members[selectedMemberId]?.name) {
        await scoreService.updateMemberName(selectedMemberId, editMemberName.trim());
      }
      
      // Update icon if it changed
      if (selectedIcon && selectedIcon !== members[selectedMemberId]?.icon) {
        await scoreService.updateMemberIcon(selectedMemberId, selectedIcon);
      }
      
      // Update score
      const scoreToSave = Math.max(0, parseInt(currentScoreInput) || 0);
      await scoreService.saveScore(scoreToSave, selectedMemberId);
      
      // Refresh data
      await loadMembers();
      if (onMembersUpdate) {
        onMembersUpdate();
      }
      
      // Close dialog
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error saving member changes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditDialog = () => {
    setEditMemberOpen(false);
    setSelectedMemberId(null);
    setCurrentScore(0);
    setCurrentScoreInput('0');
    setEditMemberName('');
    setSelectedIcon('😊');
  };

  const getEmojiIcon = (icon?: string) => {
    return icon || '😊';
  };

  const memberEntries = Object.entries(members).filter(([memberId, memberInfo]) => memberInfo !== null);

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h4">👥</Typography>
          <Typography variant="h6">
            Members
          </Typography>
        </Stack>
        
        {memberEntries.length === 0 ? (
          <Alert severity="info">
            No members yet. Add your first member to get started!
          </Alert>
        ) : (
          <List>
            {memberEntries.map(([memberId, memberInfo]) => (
              <ListItem
                key={memberId}
                divider
                secondaryAction={
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      onClick={() => handleEditMember(memberId)}
                      disabled={loading}
                      color="primary"
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    {memberEntries.length > 1 && (
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveMember(memberId)}
                        disabled={loading}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Stack>
                }
              >
                <Avatar sx={{ mr: 2, bgcolor: 'primary.main', fontSize: '1.5rem' }}>
                  {getEmojiIcon(memberInfo?.icon)}
                </Avatar>
                <ListItemText
                  primary={
                    <Typography variant="body1">{memberInfo?.name || 'Unknown'}</Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        <Box paddingTop={2}>
          {!showAddMember ? (
            <Button
              variant="contained"
              color="success"
              startIcon={loading ? <CircularProgress size={16} /> : <Add />}
              onClick={() => setShowAddMember(true)}
              disabled={loading}
              fullWidth
            >
              Add Member
            </Button>
          ) : (
            <Stack component="form" onSubmit={handleAddMember} spacing={2}>
              <TextField
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
                placeholder="Enter member name"
                slotProps={{ htmlInput: { maxLength: 30 } }}
                disabled={loading}
                autoFocus
                fullWidth
                size="small"
              />
              <Stack direction="row" spacing={1}>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={loading || !newMemberName.trim()}
                  fullWidth
                >
                  {loading ? <CircularProgress size={16} /> : 'Add'}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    setShowAddMember(false);
                    setNewMemberName('');
                  }}
                  disabled={loading}
                  fullWidth
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </CardContent>

      {/* Edit Member Dialog */}
      <Dialog open={editMemberOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Member</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Name Editing Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Member Name
              </Typography>
              <TextField
                value={editMemberName}
                onChange={(e) => setEditMemberName(e.target.value)}
                disabled={loading}
                fullWidth
                placeholder="Enter member name"
                slotProps={{ htmlInput: { maxLength: 30 } }}
              />
            </Box>

            <Divider />

            {/* Score Setting Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Current Score
              </Typography>
              <TextField
                type="number"
                value={currentScoreInput}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty string or valid numbers
                  if (value === '' || /^\d+$/.test(value)) {
                    setCurrentScoreInput(value);
                  }
                }}
                placeholder="Enter score"
                disabled={loading}
                fullWidth
                slotProps={{ 
                  htmlInput: { min: 0, step: 1 } 
                }}
              />
            </Box>

            <Divider />

            {/* Icon Selection Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Choose Icon
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 2
                }}
              >
                {Object.entries(availableEmojis).map(([emoji, emojiData]) => (
                  <Button
                    key={emoji}
                    variant={selectedIcon === emoji ? "contained" : "outlined"}
                    onClick={() => handleIconSelection(emoji)}
                    disabled={loading}
                    sx={{
                      minHeight: 60,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    <Typography variant="h4">
                      {emojiData.emoji}
                    </Typography>
                    <Typography variant="caption" sx={{ textTransform: 'none' }}>
                      {emojiData.name}
                    </Typography>
                  </Button>
                ))}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveChanges} 
            disabled={loading || !editMemberName.trim()}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default Members;